"use client";

import Link from "next/link";
import { forwardRef, type ComponentPropsWithoutRef } from "react";

import { trackCtaClick } from "@/shared/lib/analytics";
import type { CtaLocation, CtaName } from "@/shared/lib/gtm";

type CtaLinkProps = ComponentPropsWithoutRef<typeof Link> & {
  ctaName: CtaName;
  ctaLocation: CtaLocation;
};

/**
 * A `next/link` that reports itself as a CTA click.
 *
 * Tracking lives in the link rather than in a handler at each call site so a
 * CTA cannot be moved, restyled or duplicated without its tracking coming
 * along. Forwards its ref and spreads every other prop, so it drops into
 * `<Button asChild>` (Radix Slot) exactly where a plain `<Link>` was.
 */
const CtaLink = forwardRef<HTMLAnchorElement, CtaLinkProps>(function CtaLink(
  { ctaName, ctaLocation, onClick, href, ...rest },
  ref,
) {
  return (
    <Link
      ref={ref}
      href={href}
      onClick={(event) => {
        trackCtaClick(
          ctaName,
          ctaLocation,
          typeof href === "string" ? href : undefined,
        );
        onClick?.(event);
      }}
      {...rest}
    />
  );
});

export default CtaLink;
